// @ts-strict-ignore
import React, {
  useCallback, useMemo, useRef,
} from 'react';
import type { Option } from '@usealloy/component-library';
import { MultiSelect } from '@usealloy/component-library';

import { ActionType } from 'flow/store/engine/constants';
import { populateDataRouteData } from 'flow/service/dataRoutes/dataRoutes';
import type { Action, MatrixModel, PopulatedDataRoute } from 'flow/store/engine/types';
import { generatePublicId } from 'flow/service/publicIds';

import { createDataRoute, updateAction, updateDataRouteValue } from 'flow/store/redux/slice';
import {
  selectFocusedAction,
  selectNodeLineageForAnchorNode,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { isActionOfType } from '~/dashboard/views/workflows/service/actions';

import {
  getLinkableActionDataType,
  getAxisDataRouteKey,
  getAxisHeaderActionOptions,
  getAxisHeaderAttributeOptions,
  getMatrixAxisByActionDataType,
  getMultiSelectProps,
  transformActionToOption,
  updateMatrixModelWithDataTypeChange,
  isAxisDataTypeDifferent,
  filterAxisOptions,
} from './helpers';

import ActionOption from './components/Option';
import SingleValue from './components/SingleValue';

import type {
  AxisHeaderDropdownProps,
  AxisHeaderActionOption,
  AxisHeaderAttributeOption,
} from './types';
import { AttributeWidgetOption } from '../../ThresholdsTab/RuleBuilderContainer/Rule/Threshold/AttributeWidgetOption';
import { useMatrixHistoryDispatch } from '../useMatrixHistoryDispatch';
import { Highlighter } from './styles';
import { getMatrixValueHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';


export default function AxisHeaderDropdown({
  axis, isDisabled = false, action,
}: AxisHeaderDropdownProps) {
  const dispatch = useAppDispatch();
  const { actions, dataRoutes } = useAppSelector(selectWorkflowData);
  const { workflow, services, universalAttributes } = useAppSelector(selectWorkflowProps);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const focusedAction = useAppSelector(selectFocusedAction);

  // Use the provided action prop if available, otherwise fall back to focused action
  const sourceAction = action || focusedAction;
  const { publicId, matrixModel } = sourceAction;

  const handleMatrixHistoryDispatch = useMatrixHistoryDispatch(publicId);
  const handleDispatch = useCallback(
    (stateChange: () => void) => handleMatrixHistoryDispatch({
      stateChange,
      focusTarget: { axisDataroute: axis },
    }),
    [handleMatrixHistoryDispatch, axis],
  );


  const axisHeaderActionOptions: AxisHeaderActionOption[] = useMemo(
    () => getAxisHeaderActionOptions({
      actions, focusedPublicId: publicId, nodeLineage,
    }),
    [actions, nodeLineage, publicId]);

  const axisDataRouteKey = getAxisDataRouteKey(axis);
  const dataRouteId = matrixModel[axisDataRouteKey];
  const dataRoute = dataRoutes[dataRouteId];
  const targetAction = actions[dataRoute?.sourceId];

  const extendedDataRoute: PopulatedDataRoute = useMemo(() => targetAction
    && targetAction.type === ActionType.SERVICE
    && populateDataRouteData({
      actions, dataRoute, services, universalAttributes,
    }),
  [actions, dataRoute, services, targetAction, universalAttributes]);

  const attributeOptions: Option[] = useMemo(() => (extendedDataRoute)
    && getAxisHeaderAttributeOptions({
      dataRoute: extendedDataRoute,
      services,
      workflow,
      universalAttributes,
    }),
  [extendedDataRoute, services, workflow, universalAttributes]);

  const selectedActionOption: AxisHeaderActionOption = useMemo(
    () => (targetAction ? transformActionToOption(targetAction) : null), [targetAction],
  );

  // Reuse the matching menu option to keep the same label (error attribute labels are derived/not stored on the attribute)
  const selectedServiceAttributeOption = extendedDataRoute && (
    attributeOptions?.find(option => option.value === `${extendedDataRoute.attribute.id}`)
    ?? {
      label: extendedDataRoute.attribute.displayName,
      value: `${extendedDataRoute.attribute.id}`,
    }
  );

  // Dispatches directly (no history wrapper) so callers can batch it into a single
  // `handleDispatch`, keeping the whole selection in one undoable history entry.
  const updateMatrixModelAxis = useCallback((
    actionToLink: Action,
    updatedMatrixModel: MatrixModel,
  ) => {
    const dataType = getLinkableActionDataType(actionToLink);
    const originalAxisSettings = matrixModel.axis[axis.toLowerCase()];
    const newAxisSettings = getMatrixAxisByActionDataType(dataType);

    if (dataType
        && isAxisDataTypeDifferent(originalAxisSettings, newAxisSettings)
    ) {
      const updatedMatrixModelWithAxis = updateMatrixModelWithDataTypeChange({
        prev: originalAxisSettings,
        next: newAxisSettings,
        axis,
        matrixModel: updatedMatrixModel,
      });

      dispatch(updateAction({
        path: `${publicId}.matrixModel`,
        value: updatedMatrixModelWithAxis,
      }));
    }
  }, [matrixModel.axis, axis, dispatch, publicId]);

  const handleActionAxisOnChange = useCallback((actionOption: AxisHeaderActionOption) => {
    const actionToLink = actions[actionOption.value];
    const updatedMatrixModel = { ...matrixModel };
    const isNonServiceAction = !isActionOfType(actionToLink, ActionType.SERVICE);

    // if axis has no selected action:
    if (!dataRoute) {
      const newDataRoutePublicId = generatePublicId('dataroute');

      handleDispatch(() => {
        dispatch(createDataRoute({
          path: newDataRoutePublicId,
          value: {
            publicId: newDataRoutePublicId,
            sourceId: actionOption.value,
            targetId: publicId,
          },
          expressionPath: '',
        }));
        dispatch(updateAction({
          path: `${publicId}.matrixModel.${axisDataRouteKey}`,
          value: newDataRoutePublicId,
        }));
        updatedMatrixModel[axisDataRouteKey] = newDataRoutePublicId;
        if (isNonServiceAction) {
          updateMatrixModelAxis(actionToLink, updatedMatrixModel);
        }
      });
    }

    // if changing axis to a different action:
    if (dataRoute && actionOption.value !== dataRoute.sourceId) {
      handleDispatch(() => {
        dispatch(updateDataRouteValue({
          path: `${dataRoute.publicId}.sourceId`,
          value: actionOption.value,
        }));
        if (isNonServiceAction) {
          updateMatrixModelAxis(actionToLink, updatedMatrixModel);
        }
      });
    }
  }, [
    actions,
    matrixModel,
    dataRoute,
    updateMatrixModelAxis,
    handleDispatch,
    dispatch,
    publicId,
    axisDataRouteKey,
  ]);

  const handleOnAttributeChange = useCallback((
    attributeOption: AxisHeaderAttributeOption,
  ) => {
    if (+attributeOption.value === +dataRoute.attribute.id) {
      return;
    }

    handleDispatch(() => {
      dispatch(updateDataRouteValue({
        path: `${dataRoute.publicId}.attribute.id`,
        value: Number(attributeOption.value),
      }));

      const { dataType } = attributeOption.meta;
      const originalAxisSettings = matrixModel.axis[axis.toLowerCase()];
      const newAxisSettings = getMatrixAxisByActionDataType(dataType);

      if (isAxisDataTypeDifferent(originalAxisSettings, newAxisSettings)) {
        const updatedMatrixModelWithAxis = updateMatrixModelWithDataTypeChange({
          prev: originalAxisSettings,
          next: newAxisSettings,
          axis,
          matrixModel,
        });

        dispatch(updateAction({
          path: `${publicId}.matrixModel`,
          value: updatedMatrixModelWithAxis,
        }));
      }
    });
  }, [axis, dataRoute, dispatch, matrixModel, publicId, handleDispatch]);

  const multiSelectRef = useRef(null);

  const placeholderText = `Select ${axis} Axis`;

  const autoFocus = (axis === 'X' && !dataRouteId)
    || (axis === 'Y' && !isDisabled && !dataRouteId);

  return (
    <Highlighter
      id={getMatrixValueHtmlElementId({ axisDataroute: axis })}
      tabIndex={-1}
    >
      <MultiSelect
        {...getMultiSelectProps()}
        autoFocus={autoFocus}
        openMenuOnFocus
        closeMenuOnSelect
        filterOption={filterAxisOptions}
        onChange={handleActionAxisOnChange}
        options={axisHeaderActionOptions}
        selectedOptions={selectedActionOption}
        isSearchable
        isDisabled={isDisabled}
        placeholder={placeholderText}
        ref={multiSelectRef}
        components={{
          // @ts-expect-error TODO: fix this
          Option: ActionOption<AxisHeaderActionOption>,
          SingleValue,
        }}
      />
      {selectedActionOption?.meta.actionType === ActionType.SERVICE && (
        <MultiSelect
          {...getMultiSelectProps(selectedActionOption.meta.color)}
          closeMenuOnSelect
          onChange={handleOnAttributeChange}
          options={attributeOptions}
          selectedOptions={selectedServiceAttributeOption}
          isSearchable
          isDisabled={isDisabled}
          placeholder={placeholderText}
          ref={multiSelectRef}
          components={{ Option: AttributeWidgetOption }}
        />
      )}
    </Highlighter>
  );
}
